import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import * as Icons from 'lucide-react';
import { Volume2, Square, ArrowRight, ArrowLeft, X, Captions, Sparkles, Check, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LoadingSpinner } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import useNarration from '../hooks/useNarration';
import celebrate from '../utils/celebrate';
import { LogoBox, useTenantBrand } from '../context/TenantBrandContext';
import { useActiveChild } from '../context/ActiveChildContext';
import CoreIntake from '../components/welcome/CoreIntake';
import BasicsStep from '../components/welcome/BasicsStep';
import { AddChildrenStep } from '../components/welcome/AddChildrenStep';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ico = ({ name, ...p }) => { const C = Icons[name] || Icons.Sparkles; return <C {...p} />; };

const SCENES = ['welcome', 'expect', 'learn', 'create', 'patricia', 'tour', 'question', 'actions', 'lifetime'];

export default function SignatureWelcomePage() {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const narration = useNarration();

  const [archetypes, setArchetypes] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [picking, setPicking] = useState(false);
  const [intake, setIntake] = useState(false);
  const [basics, setBasics] = useState(false);
  const [secondary, setSecondary] = useState([]);
  const [children, setChildren] = useState(false);
  const [basicsDone, setBasicsDone] = useState(false);
  const { activeSubject, isSelf, canWrite, switchSubject, loading: subjectsLoading } = useActiveChild();
  // Setting up a child's FuturePath: never ask "who is this for?", we already know.
  const subjectNote = !isSelf && activeSubject ? (
    <p className="text-center text-xs text-amber-300/90 mb-5" data-testid="welcome-subject-note">
      You're setting up {activeSubject.name}'s FuturePath, everything here saves to them.
    </p>
  ) : null;
  const [forWhom, setForWhom] = useState(null);      // myself · my_child · invited
  const [code, setCode] = useState('');
  const [codeAge, setCodeAge] = useState('');
  const [codeMsg, setCodeMsg] = useState(null);
  const [i, setI] = useState(0);
  const [pick, setPick] = useState(null);
  const [captions, setCaptions] = useState(true);
  const [tourStep, setTourStep] = useState(0);
  const [beat, setBeat] = useState(null);
  const demoArchetype = params.get('archetype');

  const demo = params.get('demo') === '1';   // presenter mode: never writes anybody's state

  const track = useCallback((event, extra = {}) => {
    if (demo) return;
    axios.post(`${API}/welcome/event`, { event, ...extra }, getAuthHeader()).catch(() => {});
  }, [getAuthHeader, demo]);

  const loadConfig = useCallback(async (key) => {
    const r = await axios.get(`${API}/welcome/config${key ? `?archetype=${key}` : ''}`, getAuthHeader());
    setCfg(r.data);
    setPicking(false);
    setI(0);
    track('started', { archetype: r.data.archetype });
  }, [getAuthHeader, track]);

  useEffect(() => {
    (async () => {
      const [a, st] = await Promise.all([
        axios.get(`${API}/welcome/archetypes`, getAuthHeader()),
        axios.get(`${API}/welcome/state`, getAuthHeader()),
      ]);
      setArchetypes(a.data.archetypes);
      if (demoArchetype) return loadConfig(demoArchetype);
      // Coming back to change who they are, the promise we make during onboarding.
      if (params.get('repick') === '1') {
        setSecondary(st.data.secondary_archetypes || []);
        setForWhom('myself');
        return setPicking(true);
      }
      setBasicsDone(!!st.data.basics_complete);
      if (!isSelf) setForWhom('myself');
      if (!st.data.basics_complete) return setBasics(true);
      if (st.data.needs_archetype) return setPicking(true);
      return loadConfig(null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [chosen, setChosen] = useState(null);

  const choose = async (key) => {
    await axios.post(`${API}/welcome/primary`, { archetype: key, secondary }, getAuthHeader());
    if (refreshUser) refreshUser();
    setChosen(key);
    setPicking(false);
    setIntake(true);            // two minutes of core intake, then the welcome
  };

  const scene = SCENES[i];
  const accent = cfg?.accent || '#22d3ee';
  const second = '#a78bfa';
  const features = cfg?.features || [];

  // Patricia talks about the screen you're actually on, never the same block twice.
  const sceneScript = useMemo(() => {
    if (!cfg) return '';
    const s = cfg.scene_scripts || {};
    if (scene === 'tour') {
      const f = features[tourStep];
      if (!f) return s.tour_intro || '';
      const intro = tourStep === 0 ? `${s.tour_intro || ''} ` : '';
      return `${intro}${f.label}. ${f.line}`;
    }
    return s[scene] || cfg.narration || '';
  }, [cfg, scene, tourStep, features]);

  const say = () => {
    if (narration.speaking) return narration.stop();
    narration.say(sceneScript);
  };

  useEffect(() => { narration.stop?.(); }, [i]);   // never talk over the next scene
  useEffect(() => {
    if (!beat) return;
    const t = setTimeout(() => setBeat(null), 2600);
    return () => clearTimeout(t);
  }, [beat]);

  const BEATS = {
    learn: 'Good, that\u2019s the part everything else runs on.',
    patricia: 'You\u2019ll like having her around.',
    question: 'Nice. That goes straight to the top of your list.',
    actions: 'Alright \u2014 let\u2019s go.',
  };

  const next = () => {
    if (scene === 'tour' && tourStep < features.length - 1) return setTourStep((s) => s + 1);
    if (i < SCENES.length - 1) {
      const to = SCENES[i + 1];
      setI(i + 1);
      track('scene', { scene: to });
      if (to === 'actions') celebrate(BEATS.actions, { pieces: 18 });
      else if (BEATS[to]) setBeat(BEATS[to]);
      return;
    }
    finish();
  };
  const back = () => {
    if (scene === 'tour' && tourStep > 0) return setTourStep((s) => s - 1);
    if (i > 0) setI(i - 1);
  };

  // Parents belong on the family dashboard; everyone else on their own.
  const home = () => (user?.role === 'parent' || forWhom === 'my_child' ? '/app/family' : '/app/dashboard');

  const finish = async () => {
    // Await it: the dashboard checks this state on mount and would bounce us back.
    if (!demo) {
      try { await axios.post(`${API}/welcome/event`, { event: 'completed' }, getAuthHeader()); } catch { /* still leave */ }
    }
    celebrate('Your FuturePath is ready. Let\u2019s go.');
    nav(home());
  };

  const leave = async (event) => {
    if (!demo) {
      try { await axios.post(`${API}/welcome/event`, { event }, getAuthHeader()); } catch { /* still leave */ }
    }
    nav(home());
  };

  const skip = () => leave('skipped');

  const chooseFocus = (o) => {
    setPick(o.key);
    track('question', { value: o.key });
    setBeat(BEATS.question);
  };

  const orderedActions = useMemo(() => {
    if (!cfg) return [];
    const chosen = cfg.question?.options?.find((o) => o.key === pick)?.action;
    const list = [...(cfg.actions || [])];
    if (!chosen) return list;
    const hit = features.find((f) => f.key === chosen);
    if (hit && !list.some((a) => a.key === chosen)) {
      list.unshift({ key: chosen, title: hit.label, body: hit.line, href: hit.href, minutes: 8, because: true });
    } else {
      const k = list.findIndex((a) => a.key === chosen);
      if (k > 0) list.unshift(list.splice(k, 1)[0]);
    }
    return list.slice(0, 3);
  }, [cfg, pick, features]);

  // A teen who owns their password answers for themselves. The parent gets a clear read-only
  // explanation instead of being dropped into an onboarding that would 403 on the first answer.
  // Only when we have actually confirmed whose profile this is. A subject we can't resolve is a
  // stale selection, not a claimed teen, that must never block someone's own onboarding.
  if (!isSelf && !canWrite && activeSubject && !subjectsLoading) {
    const first = activeSubject?.name?.split(' ')[0] || 'They';
    return (
      <Shell accent="#818cf8">
        <div className="max-w-xl mx-auto text-center" data-testid="welcome-view-only">
          <Eye className="w-7 h-7 text-indigo-300 mx-auto" />
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight mt-5">
            {first}'s FuturePath is theirs to set up.
          </h1>
          <p className="text-base text-slate-300/90 mt-4 leading-relaxed">
            {first} has their own password now, so their answers have to come from them, that's the
            deal we make with every young person over 13. You can see everything they build, and
            you're still in charge of the plan and the billing.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-9">
            <button onClick={() => switchSubject(null)} data-testid="view-only-back"
              className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-6 py-3 text-sm transition-colors">
              Back to my FuturePath
            </button>
            <button onClick={() => switchSubject(activeSubject.id, '/app/family')}
              data-testid="view-only-family"
              className="rounded-full border border-white/20 hover:border-white/50 text-slate-200 px-6 py-3 text-sm transition-colors">
              See our family overview
            </button>
          </div>
        </div>
      </Shell>
    );
  }


  // ── archetype picker ───────────────────────────────────────────────────────
  if ((picking || basics) && !forWhom && !basicsDone) {
    return (
      <Shell accent="#22d3ee" onSkip={skip}>
        <div className="text-center max-w-3xl mx-auto" data-testid="for-whom">
          <p className="text-[11px] uppercase tracking-[0.24em] text-cyan-300/90">First things first</p>
          <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white tracking-tight mt-3">
            Who is FuturePath for?
          </h1>
          <p className="text-base text-slate-300/90 mt-4">
            This decides whose FuturePath we're building, and who's allowed to see it.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-10 max-w-4xl mx-auto">
          {[['myself', 'Myself', "I'm creating my own FuturePath."],
            ['my_child', 'My child', "I'm a parent or guardian creating a FuturePath for my child."],
            ['invited', 'I was given a code', 'My school, camp or programme gave me a code or link.']]
            .map(([key, label, blurb], n) => (
            <motion.button key={key} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.07 * n }} data-testid={`for-whom-${key}`}
              onClick={() => setForWhom(key)}
              className="text-left rounded-2xl border border-cyan-500/30 p-6 bg-slate-900/60 hover:-translate-y-1 transition-transform duration-200">
              <p className="font-outfit text-lg font-semibold text-white">{label}</p>
              <p className="text-sm text-slate-300 mt-2 leading-relaxed">{blurb}</p>
            </motion.button>
          ))}
        </div>
      </Shell>
    );
  }

  if ((picking || basics) && forWhom === 'invited' && !codeMsg) {
    const redeem = async () => {
      try {
        const r = await axios.post(`${API}/identity/redeem-code`,
          { code: code.trim(), age: Number(codeAge) || 18 }, getAuthHeader());
        if (r.data.can_explore) setForWhom('myself');
        else setCodeMsg(r.data.message || 'We need permission from your parent or guardian first.');
      } catch (e) {
        setCodeMsg(e?.response?.data?.detail || "That code isn't recognised.");
      }
    };
    return (
      <Shell accent="#22d3ee" onSkip={skip}>
        <div className="max-w-md mx-auto text-center" data-testid="redeem-code">
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight">Enter your code</h1>
          <p className="text-sm text-slate-300 mt-3">The one your school, camp or programme gave you.</p>
          <div className="mt-8 space-y-3">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. CAMP2027"
              className="input-dark text-center" data-testid="code-input" />
            <Input value={codeAge} onChange={(e) => setCodeAge(e.target.value)} placeholder="How old are you?"
              className="input-dark text-center" data-testid="code-age" />
            <Button onClick={redeem} data-testid="code-submit"
              className="rounded-full w-full py-6 text-white bg-gradient-to-r from-cyan-500 to-violet-500">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <button onClick={() => setForWhom(null)} className="text-xs text-slate-300 hover:text-white">Back</button>
          </div>
        </div>
      </Shell>
    );
  }

  if (codeMsg) {
    return (
      <Shell accent="#fbbf24" onSkip={skip}>
        <div className="max-w-lg mx-auto text-center" data-testid="pending-authorization">
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight">You're almost ready.</h1>
          <p className="text-base text-slate-300/90 mt-5 leading-relaxed">{codeMsg}</p>
          <p className="text-xs text-slate-300 mt-6">
            We've stopped here on purpose. Nothing else is collected until that's done.
          </p>
          <Button onClick={() => nav('/app/dashboard')} data-testid="pending-ok"
            className="rounded-full mt-8 px-7 text-white bg-gradient-to-r from-amber-500 to-orange-500">
            I understand
          </Button>
        </div>
      </Shell>
    );
  }

  if (basics) {
    return (
      <Shell accent="#22d3ee" onSkip={skip}>
        {subjectNote}
        <BasicsStep accent="#22d3ee" onDone={() => {
          setBasics(false);
          if (forWhom === 'my_child') return setChildren(true);
          setPicking(true);
        }} />
      </Shell>
    );
  }

  if (children) {
    return (
      <Shell accent="#22d3ee" onSkip={skip}>
        <AddChildrenStep accent="#22d3ee" onDone={() => { setChildren(false); setChosen('parent'); setPicking(true); }} />
      </Shell>
    );
  }

  if (picking) {
    const primary = archetypes.find((x) => x.key === chosen);
    const toggleSecond = (key) => setSecondary((cur) =>
      cur.includes(key) ? cur.filter((k) => k !== key) : cur.length >= 2 ? cur : [...cur, key]);

    // Step one: who you are right now. Step two: anything else that's also true.
    if (!chosen) {
      return (
        <Shell accent="#22d3ee" onSkip={skip}>
          {subjectNote}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-3xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.24em] text-cyan-300/90">Step 1 of 2 · Where you are</p>
            <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white tracking-tight mt-3">
              What best describes where you are right now?
            </h1>
            <p className="text-base text-slate-300/90 mt-4 max-w-2xl mx-auto leading-relaxed">
              Pick the one that fits best. Your whole FuturePath, the tools you see, the questions we ask,
              the way Patricia talks to you, is built around where you are in your life right now and
              where you're trying to go.
            </p>
            <p className="text-sm text-slate-300 mt-3">You can change it any time once you're inside.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10 max-w-6xl mx-auto" data-testid="archetype-picker">
            {archetypes.map((a, n) => (
              <motion.button key={a.key} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * n, duration: 0.35 }} onClick={() => setChosen(a.key)}
                data-testid={`archetype-${a.key}`}
                className="text-left rounded-2xl border p-6 bg-slate-900/60 hover:-translate-y-1 transition-transform duration-200"
                style={{ borderColor: `${a.accent}40`, boxShadow: `0 30px 70px -55px ${a.accent}` }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ background: `${a.accent}1f`, border: `1px solid ${a.accent}44` }}>
                  <Ico name={a.icon} className="w-5 h-5" style={{ color: a.accent }} />
                </div>
                <p className="font-outfit text-lg font-semibold text-white mt-4">{a.label}</p>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed">{a.blurb}</p>
              </motion.button>
            ))}
          </div>
        </Shell>
      );
    }

    return (
      <Shell accent={primary?.accent || '#22d3ee'} onSkip={skip}>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-3xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.24em]" style={{ color: primary?.accent || '#22d3ee' }}>
            Step 2 of 2 · Anything else true right now
          </p>
          <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white tracking-tight mt-3">
            Is any of this also you?
          </h1>
          <p className="text-base text-slate-300/90 mt-4 max-w-2xl mx-auto leading-relaxed">
            Your primary is <span className="text-white font-semibold">{primary?.label}</span>, that's the
            voice and the shape of your experience. Pick up to two more that are also true and we'll switch
            on the tools those bring with them. Optional, and changeable later.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-9 max-w-5xl mx-auto" data-testid="secondary-picker">
          {archetypes.filter((a) => a.key !== chosen).map((a, n) => {
            const on = secondary.includes(a.key);
            return (
              <motion.button key={a.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * n }} onClick={() => toggleSecond(a.key)}
                data-testid={`secondary-${a.key}`}
                className="text-left rounded-xl border p-4 transition-all duration-200 hover:-translate-y-0.5 flex items-start gap-3"
                style={on
                  ? { borderColor: a.accent, background: `${a.accent}1c`, boxShadow: `0 0 34px -18px ${a.accent}` }
                  : { borderColor: 'rgba(148,163,184,0.2)', background: 'rgba(15,23,42,0.5)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${a.accent}1f`, border: `1px solid ${a.accent}40` }}>
                  <Ico name={a.icon} className="w-4 h-4" style={{ color: a.accent }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{a.label}</p>
                  <p className="text-xs text-slate-300 mt-1 leading-relaxed">{a.blurb}</p>
                </div>
              </motion.button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
          <Button variant="ghost" onClick={() => { setChosen(null); setSecondary([]); }}
            data-testid="secondary-back" className="rounded-full text-slate-300">
            Change my primary
          </Button>
          <Button onClick={() => choose(chosen)} data-testid="secondary-continue"
            className="rounded-full px-8 py-6 text-slate-900 font-semibold"
            style={{ background: `linear-gradient(90deg, ${primary?.accent || '#22d3ee'}, #a78bfa)` }}>
            {secondary.length ? `Continue with ${secondary.length + 1} selected` : 'Continue, just my primary'}
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
        <p className="text-xs text-slate-300 text-center mt-5">
          Nothing is locked. Your path and archetype sit at the top of every screen, and you can change
          them whenever your life does.
        </p>
      </Shell>
    );
  }

  if (intake) {
    const a = archetypes.find((x) => x.key === chosen);
    return (
      <Shell accent={a?.accent || '#22d3ee'} onSkip={skip}>
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-[11px] uppercase tracking-[0.24em]" style={{ color: a?.accent || '#22d3ee' }}>
            {a?.label} · Setting you up
          </p>
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight mt-3">
            Eight quick things, then we begin.
          </h1>
          <p className="text-sm text-slate-300 mt-3">
            Enough for FuturePath to be about you rather than about people in general.
          </p>
        </div>
        <CoreIntake accent={a?.accent || '#22d3ee'}
          onDone={() => { setIntake(false); celebrate('That\u2019s all we need for now.', { pieces: 16 }); loadConfig(chosen); }} />
      </Shell>
    );
  }

  if (!cfg) return <div className="min-h-screen bg-[#0a1729] flex items-center justify-center"><LoadingSpinner size="lg" /></div>;

  const progress = Math.round(((i + 1) / SCENES.length) * 100);
  const narrationText = sceneScript;

  return (
    <Shell accent={accent} onSkip={skip} progress={progress} step={i + 1} total={SCENES.length}
      onListen={say} speaking={narration.speaking} loading={narration.loading}
      captions={captions} setCaptions={setCaptions}>
      <AnimatePresence mode="wait">
        <motion.div key={`${scene}-${tourStep}`} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }} className="max-w-5xl mx-auto"
          data-testid={`scene-${scene}`}>

          {scene === 'welcome' && (
            <div className="text-center">
              {cfg.institution && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  className="max-w-2xl mx-auto mb-8 rounded-2xl border px-5 py-4 flex items-center gap-4 text-left"
                  data-testid="institution-band"
                  style={{ borderColor: `${cfg.institution.color}44`, background: `${cfg.institution.color}0f` }}>
                  {cfg.institution.logo_url
                    ? <img src={cfg.institution.logo_url} alt={cfg.institution.name} className="h-10 w-auto shrink-0" />
                    : <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{ background: `${cfg.institution.color}22`, border: `1px solid ${cfg.institution.color}55` }}>
                        <Icons.Building2 className="w-5 h-5" style={{ color: cfg.institution.color }} />
                      </div>}
                  <div>
                    <p className="text-sm font-semibold text-white">Welcome, {cfg.institution.name} students</p>
                    <p className="text-xs text-slate-300/85 mt-1 leading-relaxed">{cfg.institution.message}</p>
                    {cfg.institution.career_center && (
                      <p className="text-xs text-slate-300 mt-1.5">Your career centre: {cfg.institution.career_center}</p>
                    )}
                  </div>
                </motion.div>
              )}
              <p className="text-[11px] uppercase tracking-[0.24em]" style={{ color: accent }}>
                {cfg.archetype_label} · Your FuturePath Welcome
              </p>
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mt-4">
                {cfg.hero_title}
              </h1>
              <p className="text-base md:text-lg text-slate-300/90 mt-6 max-w-2xl mx-auto leading-relaxed">{cfg.hero_subhead}</p>
              {cfg.thanks && <p className="text-base text-emerald-200/90 mt-4">{cfg.thanks}</p>}
              {cfg.stage_note && (
                <p className="text-sm text-slate-300 mt-6 max-w-2xl mx-auto px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03]">
                  {cfg.stage_note}
                </p>
              )}
            </div>
          )}

          {scene === 'expect' && (
            <Section title="What FuturePath does for you" accent={accent}>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cfg.expect.map((e, n) => (
                  <motion.div key={e.title} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.06 * n }} className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
                    <p className="font-outfit text-white font-semibold">{e.title}</p>
                    <p className="text-sm text-slate-300 mt-2 leading-relaxed">{e.body}</p>
                  </motion.div>
                ))}
              </div>
            </Section>
          )}

          {scene === 'learn' && (
            <Section title="What we'll learn about you" accent={accent}
              lead="Answered once, used everywhere. Nothing here is asked twice.">
              <div className="max-w-2xl mx-auto space-y-3">
                {cfg.learn.map((l, n) => (
                  <motion.div key={l} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.07 * n }}
                    className="flex items-start gap-3 rounded-xl border border-white/10 bg-slate-900/50 px-4 py-3">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
                    <span className="text-sm text-slate-200/90">{l}</span>
                  </motion.div>
                ))}
              </div>
            </Section>
          )}

          {scene === 'create' && (
            <Section title="What FuturePath will create" accent={accent}
              lead="Not reports about people like you. Documents about you.">
              <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
                {cfg.create.map((c, n) => (
                  <motion.div key={c} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.07 * n }}
                    className="rounded-2xl border p-5 bg-slate-900/60"
                    style={{ borderColor: `${accent}33` }}>
                    <Sparkles className="w-4 h-4" style={{ color: accent }} />
                    <p className="text-sm text-slate-100 mt-3 leading-relaxed">{c}</p>
                  </motion.div>
                ))}
              </div>
            </Section>
          )}

          {scene === 'patricia' && (
            <Section title="Meet Patricia" accent={second} lead="Your FuturePath AI guide.">
              <div className="max-w-3xl mx-auto text-center">
                <div className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center"
                  style={{ background: `${second}1f`, border: `1px solid ${second}55`, boxShadow: `0 0 60px -20px ${second}` }}>
                  <Sparkles className="w-9 h-9" style={{ color: second }} />
                </div>
                <p className="text-base text-slate-300/90 mt-6 leading-relaxed">
                  Patricia knows your FuturePath profile, what you told us, what we measured and what you've decided.
                  She isn't a chatbot bolted on the side: she can see the screen you're on and answer about it.
                </p>
                <p className="text-sm text-slate-300 mt-6">Things people ask her</p>
                <div className="flex flex-wrap justify-center gap-2 mt-3">
                  {(cfg.patricia_asks || []).map((q) => (
                    <span key={q} className="px-3 py-1.5 rounded-full text-sm border border-violet-400/30 bg-violet-500/10 text-violet-100">
                      {q}
                    </span>
                  ))}
                </div>
              </div>
            </Section>
          )}

          {scene === 'tour' && features[tourStep] && (
            <div>
              <p className="text-center text-[11px] uppercase tracking-[0.24em]" style={{ color: accent }}>
                Where everything lives · {tourStep + 1} of {features.length}
              </p>
              <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 mt-6 items-center" data-testid="tour-scene">
                <div>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ background: `${accent}1f`, border: `1px solid ${accent}55`, boxShadow: `0 0 50px -18px ${accent}` }}>
                    <Ico name={features[tourStep].icon} className="w-6 h-6" style={{ color: accent }} />
                  </div>
                  <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-5" data-testid="tour-feature-label">
                    {features[tourStep].label}
                  </h2>
                  <p className="text-base text-slate-300/90 mt-4 leading-relaxed">{features[tourStep].line}</p>
                  <Link to={features[tourStep].href} className="inline-flex items-center gap-2 text-sm mt-5"
                    style={{ color: accent }} data-testid="tour-open-feature">
                    Open it now <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                {/* the map: your profile feeding every engine, dimmed except the one being explained */}
                <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-300">Your FuturePath</p>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {features.map((f, n) => (
                      <button key={f.key} onClick={() => setTourStep(n)} data-testid={`tour-dot-${f.key}`}
                        className="flex items-center gap-2 rounded-xl px-3 py-2 text-left transition-all duration-300"
                        style={n === tourStep
                          ? { background: `${accent}1a`, border: `1px solid ${accent}66`, opacity: 1 }
                          : { border: '1px solid rgba(255,255,255,0.06)', opacity: 0.35 }}>
                        <Ico name={f.icon} className="w-3.5 h-3.5 shrink-0" style={{ color: n === tourStep ? accent : '#94a3b8' }} />
                        <span className="text-xs text-slate-200 truncate">{f.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {scene === 'question' && (
            <Section title={cfg.question?.prompt} accent={accent}
              lead="One answer, and we'll put it at the top of your list.">
              <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto" data-testid="welcome-question">
                {(cfg.question?.options || []).map((o) => (
                  <button key={o.key} onClick={() => chooseFocus(o)} data-testid={`focus-${o.key}`}
                    className="px-5 py-3 rounded-2xl text-sm border transition-all duration-200 hover:-translate-y-0.5"
                    style={pick === o.key
                      ? { borderColor: accent, background: `${accent}22`, color: '#fff', boxShadow: `0 0 30px -10px ${accent}` }
                      : { borderColor: 'rgba(148,163,184,0.25)', color: '#cbd5e1' }}>
                    {o.label}
                  </button>
                ))}
              </div>
            </Section>
          )}

          {scene === 'actions' && (
            <Section title="Your FuturePath starts here" accent={accent}
              lead={pick ? 'Reordered around what you just told us.' : 'Three things, in the order that works.'}>
              <div className="grid md:grid-cols-3 gap-4" data-testid="welcome-actions">
                {orderedActions.map((a, n) => (
                  <motion.div key={a.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 * n }}
                    className="rounded-2xl border p-6 bg-slate-900/60 flex flex-col"
                    style={{ borderColor: n === 0 ? `${accent}66` : 'rgba(255,255,255,0.08)',
                             boxShadow: n === 0 ? `0 30px 70px -55px ${accent}` : 'none' }}>
                    <span className="text-xs fp-num" style={{ color: accent }}>{String(n + 1).padStart(2, '0')}</span>
                    <p className="font-outfit text-white font-semibold mt-2">{a.title}</p>
                    <p className="text-sm text-slate-300 mt-2 leading-relaxed flex-1">{a.body}</p>
                    {a.minutes && <p className="text-[11px] text-slate-300 mt-3">About {a.minutes} minutes</p>}
                    <Link to={a.href} onClick={() => track('action', { value: a.key })}
                      data-testid={`welcome-action-${a.key}`}
                      className="inline-flex items-center gap-2 text-sm mt-4" style={{ color: accent }}>
                      Start <ArrowRight className="w-4 h-4" />
                    </Link>
                  </motion.div>
                ))}
              </div>
            </Section>
          )}

          {scene === 'actions' && cfg.institution?.resources?.length > 0 && (
            <div className="mt-8 max-w-3xl mx-auto rounded-2xl border border-white/10 bg-slate-900/50 p-5"
              data-testid="institution-resources">
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-300">
                Also available to you through {cfg.institution.name}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {cfg.institution.resources.map((r) => (
                  <span key={r.label || r} className="px-3 py-1.5 rounded-full text-xs border border-white/15 text-slate-200">
                    {r.label || r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {scene === 'lifetime' && (
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white">{cfg.lifetime.title}</h2>
              <p className="text-base text-slate-300/90 mt-5 leading-relaxed">{cfg.lifetime.body}</p>
              <div className="flex flex-wrap items-center justify-center gap-2 mt-8" data-testid="lifetime-stages">
                {cfg.lifetime.stages.map((s, n) => (
                  <motion.span key={s} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 * n }}
                    className="px-3 py-1.5 rounded-full text-xs border"
                    style={{ borderColor: `${accent}44`, color: '#e2e8f0' }}>
                    {s}
                  </motion.span>
                ))}
              </div>
              <p className="font-outfit text-2xl text-white mt-8">{cfg.closing}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {beat && (
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8 }} data-testid="welcome-beat"
            className="fixed left-1/2 -translate-x-1/2 bottom-24 z-20 px-5 py-2.5 rounded-full border text-sm text-white"
            style={{ borderColor: `${accent}66`, background: 'rgba(2,6,23,0.85)', boxShadow: `0 0 40px -12px ${accent}` }}>
            {beat}
          </motion.div>
        )}
      </AnimatePresence>

      {captions && narrationText && (
        <div className="max-w-3xl mx-auto mt-10 px-4 py-3 rounded-xl border border-white/10 bg-[#0a1729]/40"
          data-testid="welcome-captions">
          <p className="text-xs text-slate-300/85 leading-relaxed">{narrationText}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
        {(i > 0 || tourStep > 0) && (
          <Button variant="ghost" onClick={back} data-testid="welcome-back" className="text-slate-300 rounded-full">
            <ArrowLeft className="w-4 h-4 mr-1.5" />Back
          </Button>
        )}
        <Button onClick={next} data-testid="welcome-next" className="rounded-full px-8 py-6 text-base text-white"
          style={{ background: `linear-gradient(90deg, ${accent}, ${second})` }}>
          {i === SCENES.length - 1 ? cfg.cta : scene === 'tour' && tourStep < features.length - 1 ? 'Next' : 'Continue'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Shell>
  );
}

const Section = ({ title, lead, accent, children }) => (
  <div>
    <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white text-center tracking-tight">{title}</h2>
    {lead && <p className="text-sm md:text-base text-slate-300 text-center mt-3">{lead}</p>}
    <div className="mt-8">{children}</div>
    <div className="h-px w-24 mx-auto mt-10" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
  </div>
);

/** The partner's logo rides along into the welcome, so it still feels like their programme. */
const TenantMark = () => {
  const { tenant } = useTenantBrand();
  if (!tenant?.branding?.logo_url) return null;
  return (
    <div className="hidden sm:flex items-center gap-2 min-w-0" data-testid="welcome-tenant-mark">
      <span className="text-slate-300">·</span>
      <LogoBox src={tenant.branding.logo_url} name={tenant.name} size="sm" tone="dark"
        className="h-9 px-2.5" />
      <span className="text-xs text-slate-300 truncate">with {tenant.name}</span>
    </div>
  );
};

const Shell = ({ children, accent, onSkip, progress, step, total, onListen, speaking, loading, captions, setCaptions }) => (
  <div className="min-h-screen bg-[#0a1729] relative overflow-hidden" data-testid="signature-welcome">
    <div className="absolute -top-40 -right-32 w-[36rem] h-[36rem] rounded-full blur-3xl opacity-25"
      style={{ background: `radial-gradient(circle, ${accent}, transparent 65%)` }} />
    <div className="absolute -bottom-48 -left-32 w-[34rem] h-[34rem] rounded-full blur-3xl opacity-20"
      style={{ background: 'radial-gradient(circle, #a78bfa, transparent 65%)' }} />

    <div className="relative z-10 px-5 sm:px-8 py-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-outfit text-sm font-semibold tracking-tight text-white">
            AGENTIC <span style={{ color: accent }}>FUTUREPATH</span> AI
          </span>
          <TenantMark />
        </div>
        <div className="flex items-center gap-2">
          {onListen && (
            <>
              <Button variant="outline" size="sm" onClick={onListen} data-testid="welcome-listen"
                className="border-slate-700 text-slate-200 rounded-full">
                {loading ? <LoadingSpinner size="sm" /> : speaking ? <Square className="w-4 h-4 mr-2" /> : <Volume2 className="w-4 h-4 mr-2" />}
                {speaking ? 'Stop' : 'Listen with Patricia'}
              </Button>
              <button onClick={() => setCaptions((v) => !v)} data-testid="welcome-captions-toggle"
                title="Captions" className={`p-2 rounded-full border ${captions ? 'border-cyan-400/50 text-cyan-200' : 'border-slate-700 text-slate-300'}`}>
                <Captions className="w-4 h-4" />
              </button>
            </>
          )}
          <button onClick={onSkip} data-testid="welcome-skip"
            className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors">
            Skip <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {progress != null && (
        <div className="mt-5">
          <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1.5">
            <span>Step {step} of {total}</span><span className="fp-num">{progress}%</span>
          </div>
          <div className="fp-meter"><div className="fp-meter-fill transition-all duration-500"
            style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${accent}, #a78bfa)` }} /></div>
        </div>
      )}
    </div>

    <div className="relative z-10 px-5 sm:px-8 pb-16 pt-6">{children}</div>
  </div>
);
